/**
 * Standalone voiceover tool: type or paste a script, pick a built-in voice, and get an audio file
 * to download or put in the shared library. Each person sees only their own voiceovers.
 */

import { ArrowLeft, BookPlus, Download, Loader2, Play, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { api, type Job } from "./api";
import { appUrl } from "./base";

const audioUrl = (id: string, download = false) => appUrl(`/api/voiceovers/${id}/audio${download ? "?download=1" : ""}`);
const DRAFT = "studio:voiceover-draft";

function load<T>(key: string, fallback: T): T {
  try {
    return { ...fallback, ...JSON.parse(localStorage.getItem(key) ?? "{}") };
  } catch {
    return fallback;
  }
}

function sentences(text: string) {
  return text.split(/\n\s*\n/).flatMap((p) => p.split(/(?<=[.!?…])\s+/)).filter((s) => s.trim()).length;
}

function seconds(t: number) {
  return `${Math.floor(t / 60)}:${String(Math.round(t % 60)).padStart(2, "0")}`;
}

export function VoiceoverPage() {
  const [draft, setDraft] = useState(() => load(DRAFT, { title: "", text: "", voice: "am_michael", speed: 1, pause: 0.4 }));
  const [voices, setVoices] = useState<{ id: string; label: string }[]>([]);
  const [list, setList] = useState<Job[] | null>(null);
  const [busy, setBusy] = useState<{ what: string; job?: Job } | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [err, setErr] = useState("");
  const set = (patch: Partial<typeof draft>) =>
    setDraft((d) => {
      const n = { ...d, ...patch };
      try {
        localStorage.setItem(DRAFT, JSON.stringify(n));
      } catch {}
      return n;
    });
  const refresh = () => api.voiceovers().then(setList).catch((e) => setErr(e.message));

  useEffect(() => {
    api.voices().then(setVoices).catch(() => {});
    void refresh();
  }, []);
  // keep the list moving while something is being made elsewhere (another tab, or before a reload)
  useEffect(() => {
    if (!list?.some((j) => j.status === "queued" || j.status === "running") || busy) return;
    const t = setTimeout(refresh, 1500);
    return () => clearTimeout(t);
  }, [list, busy]);

  const run = async (preview: boolean) => {
    setErr("");
    const what = preview ? "Speaking the first sentence" : "Speaking";
    setBusy({ what });
    try {
      let job = await api.createVoiceover({ ...draft, preview });
      if (!preview) void refresh();
      while (["queued", "running"].includes(job.status)) {
        setBusy({ what, job });
        await new Promise((r) => setTimeout(r, 700));
        job = await api.job(job.id);
      }
      if (job.status !== "done") throw new Error(job.message || "failed");
      if (preview) setPreview(audioUrl(job.id));
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
      void refresh();
    }
  };

  const n = sentences(draft.text);
  const words = draft.text.split(/\s+/).filter(Boolean).length;

  return (
    <div className="projects voiceover-page">
      <header>
        <h1>Voiceover</h1>
        <a className="button" href="#/"><ArrowLeft size={15} /> Projects</a>
      </header>
      <p className="muted small">
        Turn a script into narration with a built-in voice, made on this server. Sentences are spoken in order; a blank line starts a new paragraph, with a longer pause.
      </p>
      <section className="account-box vo-form">
        <input className="vo-title" placeholder="Title (optional)" value={draft.title} maxLength={120} onChange={(e) => set({ title: e.target.value })} />
        <textarea rows={12} placeholder="Type or paste the script here." value={draft.text} maxLength={30000} onChange={(e) => set({ text: e.target.value })} />
        <div className="voice-controls">
          <label>Voice
            <select value={draft.voice} onChange={(e) => set({ voice: e.target.value })}>
              {voices.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
            </select>
          </label>
          <label>Speed <input type="number" className="num short" min={0.6} max={1.6} step={0.05} value={draft.speed} onChange={(e) => set({ speed: +e.target.value || 1 })} /></label>
          <label title="Silence between sentences; paragraphs get twice as much">
            Pause <input type="number" className="num short" min={0} max={3} step={0.1} value={draft.pause} onChange={(e) => set({ pause: Math.max(0, +e.target.value) })} /> s
          </label>
          <span className="muted small">{n} sentence{n === 1 ? "" : "s"} · about {seconds((words / 150) * 60 / draft.speed)}</span>
          <span className="grow" />
          <button disabled={!!busy || n === 0} onClick={() => run(true)}><Play size={14} /> Preview first sentence</button>
          <button className="primary" disabled={!!busy || n === 0} onClick={() => run(false)}>Generate</button>
        </div>
        {busy && (
          <p className="muted small"><Loader2 size={13} className="spin" /> {busy.what}
            {busy.job?.status === "queued" ? " (waiting for the worker)" : busy.job ? ` ${Math.round(busy.job.progress * 100)}%` : "…"}</p>
        )}
        {err && <p className="bad small">{err}</p>}
        {preview && <audio className="vo-preview" controls autoPlay src={preview} />}
      </section>

      <h3 className="vo-heading">Your voiceovers</h3>
      {list === null ? <p className="muted">Loading…</p> : list.length === 0 ? <p className="muted">None yet.</p> : (
        <div className="vo-list">
          {list.map((j) => <VoiceoverRow key={j.id} job={j} voices={voices} onChange={refresh} onReuse={() => set({ title: String(j.params.title ?? ""), text: String(j.params.text ?? ""), voice: String(j.params.voice), speed: Number(j.params.speed), pause: Number(j.params.pause) })} />)}
        </div>
      )}
    </div>
  );
}

function VoiceoverRow({ job, voices, onChange, onReuse }: { job: Job; voices: { id: string; label: string }[]; onChange: () => void; onReuse: () => void }) {
  const [msg, setMsg] = useState("");
  const [tags, setTags] = useState<string | null>(null);
  const p = job.params as Record<string, string | number>;
  const voice = voices.find((v) => v.id === p.voice)?.label.split(" (")[0] ?? p.voice;
  const save = async () => {
    try {
      const r = await api.voiceoverToLibrary(job.id, String(p.title), (tags ?? "").split(",").map((t) => t.trim()).filter(Boolean));
      setMsg(r.duplicate ? "Already in the library." : "Saved to the shared library.");
      setTags(null);
    } catch (e) {
      setMsg((e as Error).message);
    }
  };
  return (
    <div className="vo-row">
      <div className="vo-meta">
        <strong>{String(p.title)}</strong>
        <span className="muted small">
          {voice} · {job.output?.duration ? seconds(job.output.duration) : "…"} · {new Date(job.created * 1000).toLocaleString()}
        </span>
      </div>
      {job.status === "done" ? (
        <>
          <audio controls preload="none" src={audioUrl(job.id)} />
          <div className="vo-actions">
            <a className="button" href={audioUrl(job.id, true)} title="Download the .m4a"><Download size={13} /></a>
            {tags === null ? (
              <button onClick={() => setTags("")} title="Put a copy in the shared library, for use in projects"><BookPlus size={13} /> Library</button>
            ) : (
              <form onSubmit={(e) => { e.preventDefault(); void save(); }} className="vo-tags">
                <input autoFocus placeholder="tags, comma separated" value={tags} onChange={(e) => setTags(e.target.value)} />
                <button className="primary">Save</button>
                <button type="button" onClick={() => setTags(null)}>Cancel</button>
              </form>
            )}
            <button onClick={onReuse} title="Put this script and voice back in the editor above">Reuse script</button>
            <button className="danger" title="Delete this voiceover (a copy saved to the library stays)"
              onClick={() => confirm(`Delete "${p.title}"?`) && api.deleteVoiceover(job.id).then(onChange)}><Trash2 size={13} /></button>
          </div>
        </>
      ) : job.status === "error" ? (
        <span className="bad small">Failed: {job.message}</span>
      ) : (
        <span className="muted small"><Loader2 size={13} className="spin" /> {job.status === "queued" ? "waiting" : `${Math.round(job.progress * 100)}%`}</span>
      )}
      {msg && <span className="good small">{msg}</span>}
    </div>
  );
}
